/**
 * WireVoD resumable upload client: create asset → multipart session →
 * direct-to-storage part PUTs (presigned) → verified completion.
 * Framework-free (browser or Worker); network is injectable for tests.
 */

export interface UploadSource {
  size: number;
  /** Returns bytes [start, end); end-exclusive. */
  slice(start: number, end: number): Promise<ArrayBuffer>;
}

export function bytesSource(data: Uint8Array): UploadSource {
  return {
    size: data.byteLength,
    async slice(start, end) {
      return data.slice(start, end).buffer;
    },
  };
}

export interface UploadClientOptions {
  /** e.g. https://api.wirevod.online */
  apiBase: string;
  getToken: () => string | Promise<string>;
  fetch?: typeof fetch;
  /** Parallel part uploads (default 3). */
  concurrency?: number;
  /** Attempts per part before failing (default 3). */
  retries?: number;
  /** How many URL re-signs a part may trigger on signature expiry (default 1). */
  resignBudget?: number;
  /** Delay before retry attempt N (1-based); default exponential 250ms * 2^(n-1). */
  backoffMs?: (attempt: number) => number;
  onProgress?: (progress: UploadProgress) => void;
  /**
   * Called once the upload session exists, before any bytes are sent. Persist
   * `uploadId` to resume the upload after a page reload (`resumeUpload`).
   */
  onSession?: (session: UploadSessionInfo) => void;
  /** Aborting stops part uploads; the upload stays resumable (`aborted` error). */
  signal?: AbortSignal;
}

export interface UploadSessionInfo {
  uploadId: string;
  assetId: string;
  totalBytes: number;
  totalParts: number;
}

export interface UploadProgress {
  uploadedBytes: number;
  totalBytes: number;
  completedParts: number;
  totalParts: number;
}

export interface UploadOutcome {
  uploadId: string;
  assetId: string;
  bytes: number;
  parts: number;
}

export class UploadClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status?: number,
    readonly uploadId?: string,
  ) {
    super(message);
    this.name = "UploadClientError";
  }
}

export interface StartUploadInput {
  source: UploadSource;
  title?: string;
  sha256?: string;
  /** Package the asset DRM-protected (fixed at creation). */
  drm?: boolean;
}

interface PartUrl {
  number: number;
  url: string;
}

interface SessionResponse {
  upload: {
    id: string;
    asset_id: string;
    part_size: number;
    part_count: number;
    size_bytes: number;
    parts: PartUrl[];
  };
}

interface StatusResponse {
  upload: {
    id: string;
    asset_id: string;
    status: string;
    part_size: number;
    part_count: number;
    size_bytes: number;
    uploaded_parts: Array<{ number: number; size: number; etag: string }>;
    missing_part_urls: PartUrl[];
  };
}

interface CompleteResponse {
  upload: { id: string; status: string } | null;
  asset: { id: string } | null;
}

const DEFAULT_CONCURRENCY = 3;
const DEFAULT_RETRIES = 3;

export async function uploadAsset(
  options: UploadClientOptions,
  input: StartUploadInput,
): Promise<UploadOutcome> {
  const fetchImpl = options.fetch ?? fetch;
  const asset = await apiRequest<{ asset: { id: string } }>(
    options,
    fetchImpl,
    "POST",
    "/v1/assets",
    { title: input.title, ...(input.drm ? { drm: true } : {}) },
  );
  const session = await apiRequest<SessionResponse>(options, fetchImpl, "POST", "/v1/uploads", {
    asset_id: asset.asset.id,
    size_bytes: input.source.size,
    sha256: input.sha256,
  });
  options.onSession?.({
    uploadId: session.upload.id,
    assetId: session.upload.asset_id,
    totalBytes: session.upload.size_bytes,
    totalParts: session.upload.part_count,
  });

  const parts = await uploadParts(options, fetchImpl, session.upload.id, {
    partSize: session.upload.part_size,
    partCount: session.upload.part_count,
    sizeBytes: session.upload.size_bytes,
    urls: new Map(session.upload.parts.map((part) => [part.number, part.url])),
    source: input.source,
  });
  await completeUpload(options, fetchImpl, session.upload.id, parts);
  return {
    uploadId: session.upload.id,
    assetId: session.upload.asset_id,
    bytes: session.upload.size_bytes,
    parts: session.upload.part_count,
  };
}

/** Continues an upload started earlier (or interrupted): uploads only missing parts. */
export async function resumeUpload(
  options: UploadClientOptions,
  uploadId: string,
  source: UploadSource,
): Promise<UploadOutcome> {
  const fetchImpl = options.fetch ?? fetch;
  const status = await apiRequest<StatusResponse>(
    options,
    fetchImpl,
    "GET",
    `/v1/uploads/${uploadId}`,
  );
  if (status.upload.status !== "created") {
    throw new UploadClientError(
      "upload_not_pending",
      `Upload is '${status.upload.status}'`,
      409,
      uploadId,
    );
  }

  const parts = await uploadParts(options, fetchImpl, uploadId, {
    partSize: status.upload.part_size,
    partCount: status.upload.part_count,
    sizeBytes: status.upload.size_bytes,
    urls: new Map(status.upload.missing_part_urls.map((part) => [part.number, part.url])),
    alreadyUploaded: status.upload.uploaded_parts.length,
    source,
  });
  await completeUpload(options, fetchImpl, uploadId, parts);
  return {
    uploadId,
    assetId: status.upload.asset_id,
    bytes: status.upload.size_bytes,
    parts: status.upload.part_count,
  };
}

// --- internals ----------------------------------------------------------------

function throwIfAborted(signal: AbortSignal | undefined, uploadId?: string): void {
  if (signal?.aborted) {
    throw new UploadClientError("aborted", "Upload paused or canceled", undefined, uploadId);
  }
}

interface UploadPass {
  partSize: number;
  partCount: number;
  sizeBytes: number;
  urls: Map<number, string>;
  alreadyUploaded?: number;
  source: UploadSource;
  /** In-flight URL refresh shared by all part workers (one status call per expiry wave). */
  refreshing?: Promise<void>;
}

interface UploadedPart {
  number: number;
  etag: string | null;
  sha256: string;
}

async function uploadParts(
  options: UploadClientOptions,
  fetchImpl: typeof fetch,
  uploadId: string,
  pass: UploadPass,
): Promise<UploadedPart[]> {
  const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
  const retries = options.retries ?? DEFAULT_RETRIES;
  const backoff = options.backoffMs ?? ((attempt) => 250 * 2 ** (attempt - 1));

  let completedBytes = (pass.alreadyUploaded ?? 0) * pass.partSize;
  let completedParts = pass.alreadyUploaded ?? 0;
  const results: UploadedPart[] = [];
  const report = () =>
    options.onProgress?.({
      uploadedBytes: Math.min(completedBytes, pass.sizeBytes),
      totalBytes: pass.sizeBytes,
      completedParts,
      totalParts: pass.partCount,
    });

  const pending = allNumbers(pass.partCount).filter((number) => pass.urls.has(number));
  const queue = [...pending];

  const worker = async (): Promise<void> => {
    for (;;) {
      throwIfAborted(options.signal, uploadId);
      const number = queue.shift();
      if (number === undefined) return;
      const uploaded = await uploadOnePart(
        options,
        fetchImpl,
        uploadId,
        number,
        pass,
        retries,
        backoff,
      );
      results.push(uploaded);
      completedBytes += uploaded.bytes;
      completedParts += 1;
      report();
    }
  };

  report();
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
  return results.sort((a, b) => a.number - b.number);
}

interface PartUploadResult extends UploadedPart {
  bytes: number;
}

async function uploadOnePart(
  options: UploadClientOptions,
  fetchImpl: typeof fetch,
  uploadId: string,
  partNumber: number,
  pass: UploadPass,
  retries: number,
  backoff: (attempt: number) => number,
): Promise<PartUploadResult> {
  const start = (partNumber - 1) * pass.partSize;
  const end = Math.min(start + pass.partSize, pass.sizeBytes);
  const bytes = await pass.source.slice(start, end);
  const sha256 = await sha256Hex(bytes);
  let resignBudget = options.resignBudget ?? 1;

  for (let attempt = 1; ; attempt++) {
    const url = pass.urls.get(partNumber);
    if (!url) {
      // Signature expired server-side policy requires re-sign; fetch fresh URLs.
      if (resignBudget <= 0) {
        throw new UploadClientError(
          "missing_part_url",
          `No URL for part ${partNumber}`,
          undefined,
          uploadId,
        );
      }
      resignBudget -= 1;
      await refreshPartUrl(options, fetchImpl, uploadId, pass, partNumber);
      continue;
    }
    try {
      throwIfAborted(options.signal, uploadId);
      const response = await fetchImpl(url, { method: "PUT", body: bytes, signal: options.signal });
      if (response.status === 403) {
        if (resignBudget <= 0) {
          throw new UploadClientError(
            "part_forbidden",
            `Part ${partNumber} PUT rejected`,
            403,
            uploadId,
          );
        }
        resignBudget -= 1;
        // Refresh only if no other worker has already replaced this URL.
        if (pass.urls.get(partNumber) === url) {
          pass.urls.delete(partNumber);
          await refreshPartUrl(options, fetchImpl, uploadId, pass, partNumber);
        }
        continue;
      }
      if (!response.ok) {
        const retryable =
          response.status >= 500 || response.status === 408 || response.status === 429;
        if (retryable && attempt <= retries) {
          await sleep(backoff(attempt));
          continue;
        }
        throw new UploadClientError(
          "part_failed",
          `Part ${partNumber} upload failed with ${response.status}`,
          response.status,
          uploadId,
        );
      }
      const etag = response.headers.get("etag");
      return { number: partNumber, etag, sha256, bytes: end - start };
    } catch (error) {
      if (error instanceof UploadClientError) throw error;
      throwIfAborted(options.signal, uploadId);
      if (attempt > retries) {
        throw new UploadClientError(
          "part_failed",
          `Part ${partNumber} upload failed after ${attempt} attempts`,
          undefined,
          uploadId,
        );
      }
      await sleep(backoff(attempt));
    }
  }
}

async function refreshPartUrl(
  options: UploadClientOptions,
  fetchImpl: typeof fetch,
  uploadId: string,
  pass: UploadPass,
  partNumber: number,
): Promise<void> {
  pass.refreshing ??= (async () => {
    try {
      const status = await apiRequest<StatusResponse>(
        options,
        fetchImpl,
        "GET",
        `/v1/uploads/${uploadId}`,
      );
      for (const part of status.upload.missing_part_urls) {
        pass.urls.set(part.number, part.url);
      }
    } finally {
      pass.refreshing = undefined;
    }
  })();
  await pass.refreshing;
  if (!pass.urls.has(partNumber)) {
    throw new UploadClientError(
      "part_url_unavailable",
      `No fresh URL for part ${partNumber} after refresh`,
      undefined,
      uploadId,
    );
  }
}

async function completeUpload(
  options: UploadClientOptions,
  fetchImpl: typeof fetch,
  uploadId: string,
  parts: UploadedPart[],
): Promise<void> {
  const body = {
    parts: parts.map((part) => ({
      number: part.number,
      ...(part.etag ? { etag: part.etag } : {}),
      sha256: part.sha256,
    })),
  };
  await apiRequest<CompleteResponse>(
    options,
    fetchImpl,
    "POST",
    `/v1/uploads/${uploadId}/complete`,
    body,
  );
}

async function apiRequest<T>(
  options: UploadClientOptions,
  fetchImpl: typeof fetch,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await options.getToken();
  const headers = new Headers({ authorization: `Bearer ${token}` });
  if (body !== undefined) headers.set("content-type", "application/json");
  throwIfAborted(options.signal);
  let response: Response;
  try {
    response = await fetchImpl(`${options.apiBase}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: options.signal,
    });
  } catch (error) {
    throwIfAborted(options.signal);
    throw error;
  }
  if (!response.ok) {
    let code = "api_error";
    let detail = "";
    try {
      const text = await response.text();
      detail = text.slice(0, 300);
      const payload = JSON.parse(text) as { code?: string; message?: string };
      if (typeof payload.code === "string") code = payload.code;
      if (typeof payload.message === "string") detail = payload.message;
    } catch {
      // non-JSON error body
    }
    throw new UploadClientError(
      code,
      `${method} ${path} failed with ${response.status}${detail ? `: ${detail}` : ""}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

function allNumbers(partCount: number): number[] {
  return Array.from({ length: partCount }, (_, index) => index + 1);
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
